import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { UserService } from '../../services/user.service';
import { InvitationService, InvitationDTO } from '../../services/invitation.service';
import { Subscription, interval } from 'rxjs';
import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.sass'],
    standalone: false
})
export class ToolbarComponent implements OnInit, OnDestroy {
  @Output() menuClick = new EventEmitter<void>();
  
  showMenuButton = true;
  userName: string | null = null;
  private username: string | null = null;
  invitations: InvitationDTO[] = [];
  private invitationsPollingSubscription?: Subscription;
  private loginSubscription?: Subscription;
  private tokenUpdatedSubscription?: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private invitationService: InvitationService,
    private authService: AuthService
  ) {}

  private updateMenuVisibility(url: string) {
    const emailAllowsMenu = this.authService.getEmailConfirmedClaim() !== false;
    this.showMenuButton = emailAllowsMenu && !['/', '/login', '/condominium/create', '/callback', '/condominiums', '/signup'].includes(url);
  }

  ngOnInit() {
    // Initial evaluation
    this.updateMenuVisibility(this.router.url);
    
    // Subscribe to route changes to determine if menu button should be shown
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.updateMenuVisibility(event.url);
      // Get user name if available
      this.userService.getCurrentUser().then(user => {
        this.userName = user ? user.name : null;
        this.username = user ? user.username : null;
        if (this.username) {
          this.fetchInvitations(this.username);
        }
      });
    });

    // Initialize user and start invitations polling
    this.userService.getCurrentUser().then(user => {
      this.userName = user ? user.name : null;
      this.username = user ? user.username : null;
      if (this.username) {
        this.fetchInvitations(this.username);
        this.invitationsPollingSubscription = interval(5 * 60 * 1000).subscribe(() => {
          this.fetchInvitations(this.username!);
        });
      }
    });

    // Listen for login completion events
    this.loginSubscription = this.authService.login$.subscribe(() => {
      this.updateMenuVisibility(this.router.url);
      if (this.username) {
        this.fetchInvitations(this.username);
      } else {
        this.userService.getCurrentUser().then(user => {
          this.userName = user ? user.name : null;
          this.username = user ? user.username : null;
          if (this.username) {
            this.fetchInvitations(this.username);
          }
        });
      }
    });

    this.tokenUpdatedSubscription = this.authService.tokenUpdated$.subscribe(() => {
      this.updateMenuVisibility(this.router.url);
    });
  }

  onMenuClick() {
    this.menuClick.emit();
  }

  private getCondominiumIdFromRoute(): number | null {
    let current: ActivatedRoute | null = this.route.root;
    while (current) {
      const idParam = current.snapshot.paramMap.get('id');
      if (idParam) {
        const id = Number(idParam);
        return Number.isFinite(id) && id !== 0 ? id : null;
      }
      current = current.firstChild;
    }
    return null;
  }

  private fetchInvitations(username: string) {
    const condominiumId = this.getCondominiumIdFromRoute();
    this.invitationService.getInvitationByUsername(username, condominiumId).subscribe((invitations) => {
      this.invitations = (invitations || []).filter(inv => {
        const now = new Date();
        const expiration = new Date(inv.expirationTime);
        return !inv.deleted && !inv.accepted && expiration > now;
      });
    });
  }

  onInvitationClick(invitation: InvitationDTO) {
    this.invitations = this.invitations.filter(inv => inv.id !== invitation.id);
    this.router.navigate(['/condominiums/invitation', invitation.id]);
  }

  onLogout() {
    this.authService.logout();
  }

  ngOnDestroy(): void {
    if (this.invitationsPollingSubscription) {
      this.invitationsPollingSubscription.unsubscribe();
    }
    if (this.loginSubscription) {
      this.loginSubscription.unsubscribe();
    }
    if (this.tokenUpdatedSubscription) {
      this.tokenUpdatedSubscription.unsubscribe();
    }
  }
}
